import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiCalendar, FiCheckCircle, FiChevronDown, FiUsers } from "react-icons/fi";
import http from "../../../api/apiClient";
import { useCan } from "../../../authz/useCan";
import { formatDutchDate } from "../../../utils/dutchDate";
import { euro, monthLabel, schoolYearLabel, type ContributionCell, type SchoolYearOption } from "../../../pages/finance/contributionsModel";

type ChildContribution = {
  childId: string;
  firstName?: string | null;
  lastName?: string | null;
  cells: ContributionCell[];
  expectedToDate: number;
  expectedYear: number;
  paid: number;
  balance: number;
  /** Due this month, a different contribution included. */
  monthlyFee?: number | null;
};

type Payment = { date: string; amount: number; method: "BANK" | "CASH"; termMonth?: string | null; childId?: string | null; divided: boolean };

export type ParentContributions = {
  schoolYears: SchoolYearOption[];
  schoolYear?: SchoolYearOption | null;
  monthlyFee?: number | null;
  months: string[];
  currentMonth?: string;
  familyId: string;
  familyName?: string | null;
  expectedToDate?: number | null;
  received?: number | null;
  balance?: number | null;
  surplus?: number | null;
  children: ChildContribution[];
  payments: Payment[];
};

const dotClass: Record<ContributionCell["status"], string> = {
  PAID: "bg-emerald-300 dark:bg-emerald-400/60",
  PARTIAL: "bg-amber-300 dark:bg-amber-400/60",
  OPEN: "bg-rose-300 dark:bg-rose-400/60",
  UPCOMING: "bg-slate-200 dark:bg-slate-600",
  NOT_ENROLLED: "bg-transparent",
  EXEMPT: "bg-violet-200 dark:bg-violet-400/40",
};

const statusTextClass: Record<ContributionCell["status"], string> = {
  PAID: "text-emerald-700 dark:text-emerald-300",
  PARTIAL: "text-amber-700 dark:text-amber-300",
  OPEN: "text-rose-700 dark:text-rose-300",
  UPCOMING: "text-slate-400 dark:text-slate-500",
  NOT_ENROLLED: "text-slate-400",
  EXEMPT: "text-violet-600 dark:text-violet-300",
};

/** Amounts are set in a receipt-like typeface. */
const receipt = "font-receipt tabular-nums";

type Props = {
  familyId?: string | null;
  /** Shown instead when the parent may not see the contribution, or while it cannot be loaded. */
  fallback?: ReactNode;
};

/**
 * The yearly contribution of the family in the parent portal. With the permission "parent.contributions" every child
 * can be unfolded to see which terms are paid, followed by the payments received. Read only.
 */
export default function ParentContributionsSection({ familyId, fallback = null }: Props): ReactElement | null {
  const { t } = useTranslation();
  const canView = useCan("parent.contributions", "view");
  const [schoolYearId, setSchoolYearId] = useState("");
  const [data, setData] = useState<ParentContributions | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [openChildren, setOpenChildren] = useState<Set<string>>(new Set());
  const [showPayments, setShowPayments] = useState(false);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    setState((current) => (current === "ready" ? current : "loading"));
    const params: Record<string, string> = {};
    if (familyId) params.familyId = familyId;
    if (schoolYearId) params.schoolYearId = schoolYearId;
    http.get("/parent/contributions", { params })
      .then(({ data: response }) => {
        if (cancelled) return;
        // Not opened for parents yet by the finance department: show the plain yearly fee instead.
        setData(response && response.visible !== false && Array.isArray(response.children) ? response : null);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [canView, familyId, schoolYearId]);

  if (!canView || state === "loading" || !data || data.children.length === 0) {
    return (
      <>
        {fallback}
        {canView && state === "error" && (
          <p role="alert" className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:bg-rose-950/25 dark:text-rose-200">
            {t("parentPortal.contributions.load_error", { defaultValue: "The parent contribution could not be loaded. Please try again later." })}
          </p>
        )}
      </>
    );
  }

  const balance = Number(data.balance ?? 0);
  const fee = Number(data.monthlyFee ?? 0);
  const childFee = (child: ChildContribution) => Number(child.monthlyFee ?? fee);
  const sameFee = data.children.every((child) => childFee(child) === fee);
  const childNames = new Map(data.children.map((child) => [child.childId, child.firstName || ""]));
  const currentMonthLabel = data.currentMonth ? monthLabel(data.currentMonth, "long") : "";

  const toggleChild = (childId: string) => setOpenChildren((current) => {
    const next = new Set(current);
    if (next.has(childId)) next.delete(childId);
    else next.add(childId);
    return next;
  });

  const termStatus = (cell: ContributionCell): string => {
    switch (cell.status) {
      case "PAID": return t("parentPortal.contributions.term_paid", { defaultValue: "Paid" });
      case "PARTIAL": return t("parentPortal.contributions.term_partial", { amount: euro(Number(cell.expected) - Number(cell.paid)), defaultValue: "Partly paid · {{amount}} to go" });
      case "OPEN": return t("parentPortal.contributions.term_open", { defaultValue: "Still to pay" });
      case "UPCOMING": return t("parentPortal.contributions.term_upcoming", { defaultValue: "Later this year" });
      case "EXEMPT": return t("parentPortal.contributions.term_exempt", { defaultValue: "Nothing due" });
      default: return t("parentPortal.contributions.term_not_enrolled", { defaultValue: "Not enrolled" });
    }
  };

  return (
    <section aria-labelledby="parent-contributions-title"
      className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true">
            <FiUsers />
          </span>
          <h3 id="parent-contributions-title" className="text-lg font-black text-slate-950 dark:text-slate-50">
            {t("parentPortal.yearly_fee", { defaultValue: "Yearly fee" })}
          </h3>
        </div>
        {data.schoolYears.length > 1 && (
          <select
            value={data.schoolYear?.id ?? ""}
            onChange={(event) => setSchoolYearId(event.target.value)}
            aria-label={t("parentPortal.contributions.school_year", { defaultValue: "School year" })}
            className="min-h-[40px] rounded-2xl border border-slate-200 bg-white/85 px-3 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-200"
          >
            {data.schoolYears.map((year) => <option key={year.id} value={year.id}>{schoolYearLabel(year)}</option>)}
          </select>
        )}
      </div>

      <div className="space-y-3">
        <div className={`flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-semibold ring-1 ${balance < 0
          ? "bg-rose-50 text-rose-800 ring-rose-100 dark:bg-rose-950/25 dark:text-rose-200 dark:ring-rose-900/40"
          : "bg-emerald-50 text-emerald-800 ring-emerald-100 dark:bg-emerald-950/25 dark:text-emerald-200 dark:ring-emerald-900/40"}`}>
          {balance < 0 ? <FiAlertCircle aria-hidden="true" className="mt-0.5 shrink-0" /> : <FiCheckCircle aria-hidden="true" className="mt-0.5 shrink-0" />}
          <span>
            <span className="block text-base font-black">
              {balance < 0
                ? t("parentPortal.contributions.still_due", { amount: euro(-balance), defaultValue: "Still to pay: {{amount}}" })
                : Number(data.surplus ?? 0) > 0
                  ? t("parentPortal.contributions.paid_ahead", { amount: euro(data.surplus), defaultValue: "Everything paid, {{amount}} in advance" })
                  : t("parentPortal.contributions.all_paid", { month: currentMonthLabel, defaultValue: "Everything paid up to {{month}}" })}
            </span>
            <span className="mt-0.5 block font-medium opacity-80">
              {sameFee
                ? t("parentPortal.contributions.totals", {
                  expected: euro(data.expectedToDate),
                  paid: euro(data.received),
                  fee: euro(fee),
                  defaultValue: "Due up to now {{expected}} · paid {{paid}} · {{fee}} per child per month",
                })
                : t("parentPortal.contributions.totals_short", {
                  expected: euro(data.expectedToDate),
                  paid: euro(data.received),
                  defaultValue: "Due up to now {{expected}} · paid {{paid}}",
                })}
            </span>
          </span>
        </div>

        {data.children.map((child) => {
          const open = openChildren.has(child.childId);
          const termsId = `contribution-terms-${child.childId}`;
          const childOpen = -Number(child.balance ?? 0);
          return (
            <div key={child.childId} data-child={child.childId}
              className="overflow-hidden rounded-2xl border border-slate-100 bg-white/70 shadow-sm dark:border-slate-800 dark:bg-slate-950/30">
              <button
                type="button"
                onClick={() => toggleChild(child.childId)}
                aria-expanded={open}
                aria-controls={termsId}
                aria-label={t("parentPortal.contributions.show_terms", { name: child.firstName || "", defaultValue: "Terms for {{name}}" })}
                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-slate-800 transition hover:bg-slate-50/80 dark:text-slate-100 dark:hover:bg-slate-900/40"
              >
                <FiChevronDown aria-hidden="true" className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                <span className="min-w-0 flex-1 truncate">{child.firstName}</span>
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${childOpen > 0
                  ? "bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-200"
                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"}`}>
                  {childOpen > 0
                    ? t("parentPortal.contributions.child_open", { amount: euro(childOpen), defaultValue: "{{amount}} open" })
                    : t("parentPortal.contributions.child_all_paid", { defaultValue: "All paid" })}
                </span>
                <span className={`shrink-0 text-emerald-700 dark:text-emerald-300 ${receipt}`}>{euro(childFee(child))}</span>
              </button>
              {open && (
                <ol id={termsId} className="border-t border-dashed border-slate-200 px-4 py-2 dark:border-slate-700">
                  {child.cells.filter((cell) => cell.status !== "NOT_ENROLLED").map((cell) => (
                    <li key={cell.month} data-month={cell.month}
                      className={`flex items-center gap-3 py-1.5 text-sm ${cell.month === data.currentMonth ? "font-bold" : ""}`}>
                      <span aria-hidden="true" className={`h-2.5 w-2.5 shrink-0 rounded-full ${dotClass[cell.status]}`} />
                      <span className="w-36 shrink-0 capitalize text-slate-700 dark:text-slate-200">{monthLabel(cell.month, "long")}</span>
                      <span className={`min-w-0 flex-1 truncate text-xs font-semibold ${statusTextClass[cell.status]}`}>{termStatus(cell)}</span>
                      <span className={`shrink-0 ${receipt} ${cell.status === "UPCOMING" ? "text-slate-400 dark:text-slate-500" : cell.status === "OPEN" ? "text-rose-700 dark:text-rose-300" : "text-slate-900 dark:text-slate-100"}`}>
                        {euro(cell.status === "PAID" || cell.status === "PARTIAL" ? cell.paid : cell.expected)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          );
        })}

        <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-950 px-4 py-4 text-base font-black text-white shadow-sm dark:bg-emerald-950/40 dark:text-emerald-50 dark:shadow-none dark:ring-1 dark:ring-emerald-800/60">
          <span>{t("parentPortal.total", { defaultValue: "Total" })}</span>
          <span className={receipt}>{euro(data.children.reduce((sum, child) => sum + childFee(child), 0))}</span>
        </div>

        <div className="pt-1">
          <button
            type="button"
            onClick={() => setShowPayments((current) => !current)}
            aria-expanded={showPayments}
            aria-controls="parent-contribution-payments"
            className="flex items-center gap-2 rounded-xl px-1 py-1 text-sm font-black text-slate-900 transition hover:text-emerald-700 dark:text-slate-100 dark:hover:text-emerald-300"
          >
            <FiCalendar aria-hidden="true" />
            {t("parentPortal.contributions.payments_toggle", { count: data.payments.length, defaultValue: "Payments received ({{count}})" })}
            <FiChevronDown aria-hidden="true" className={`text-slate-400 transition-transform ${showPayments ? "rotate-180" : ""}`} />
          </button>
          {showPayments && (
            <div id="parent-contribution-payments" className="mt-2">
              {data.payments.length === 0 ? (
                <p className="rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold italic text-slate-500 ring-1 ring-slate-100 dark:bg-slate-950/30 dark:ring-slate-800">
                  {t("parentPortal.contributions.no_payments", { defaultValue: "No payments received yet this school year." })}
                </p>
              ) : (
                <ul className="divide-y divide-dashed divide-slate-200 rounded-2xl border border-slate-100 bg-white/70 dark:divide-slate-700 dark:border-slate-800 dark:bg-slate-950/30">
                  {data.payments.map((payment, index) => (
                    <li key={`${payment.date}-${index}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                      <span className="font-semibold text-slate-700 dark:text-slate-200">
                        {formatDutchDate(payment.date)}
                        <span className="ml-2 text-xs font-medium text-slate-500">
                          {payment.method === "CASH"
                            ? t("parentPortal.contributions.cash", { defaultValue: "cash" })
                            : t("parentPortal.contributions.bank", { defaultValue: "bank transfer" })}
                          {payment.termMonth ? ` · ${t("parentPortal.contributions.term", { month: monthLabel(payment.termMonth, "long"), defaultValue: "for {{month}}" })}` : ""}
                          {payment.childId && childNames.get(payment.childId) ? ` · ${childNames.get(payment.childId)}` : ""}
                          {payment.divided ? ` · ${t("parentPortal.contributions.divided", { defaultValue: "part of a larger payment" })}` : ""}
                        </span>
                      </span>
                      <span className={`font-bold text-slate-900 dark:text-slate-100 ${receipt}`}>{euro(payment.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
